import type { ReactNode } from 'react';
import { Empty } from 'antd';
import ChartResults from '../chart/chartResults';
import { LazyMount } from '../chart/LazyMount';
import { CurveSeries, Page4Process, TargetRockPage4View } from '../../@types/ghePage4/types';

type Props = {
  rock: TargetRockPage4View;
  renderSelection?: (process: Page4Process) => ReactNode;
};

// Distinct, colorblind-safe categorical palette (Okabe-Ito based) so each
// analogue line is easy to tell apart. best_fit_analogues stays emphasized black.
const BEST_FIT_COLOR = '#090B0A';
const ANALOGUE_PALETTE = [
  '#0072B2', // blue
  '#E69F00', // orange
  '#009E73', // green
  '#D55E00', // vermillion
  '#CC79A7', // pink
  '#56B4E9', // sky blue
  '#B07AA1', // mauve
  '#9C755F' // brown
];

const metricLabelMap: Record<string, string> = {
  krw: 'krw',
  krco2: 'krco2',
  pc: 'pc',
  frac_flow: 'frac_flow'
};

const mapSeries = (series: CurveSeries[]) => {
  const filtered = series.filter(curve => !curve.label.startsWith('ML'));

  // Color by analogue source (not per line), so krw/krco2 of the same analogue
  // share a color and are distinguished by dash style.
  const analogueSources = Array.from(
    new Set(
      filtered
        .map(curve => curve.label.split('::')[0])
        .filter(source => source !== 'best_fit_analogues')
    )
  );

  return filtered.map(curve => {
    const [sourceLabel, metric = 'curve'] = curve.label.split('::');
    const isBestFit = sourceLabel === 'best_fit_analogues';
    const metricLabel = metricLabelMap[metric] || metric;
    const color = isBestFit
      ? BEST_FIT_COLOR
      : ANALOGUE_PALETTE[
          analogueSources.indexOf(sourceLabel) % ANALOGUE_PALETTE.length
        ];
    return {
      name: `${sourceLabel} ${metricLabel}`.trim(),
      color,
      dashStyle: metric === 'krco2' ? 'ShortDash' : 'Solid',
      lineWidth: isBestFit ? 2.5 : 1.5,
      data: curve.x_values.map((x, index) => ({
        x,
        y: curve.y_values[index]
      }))
    };
  });
};

export const GheNormalizedCurvesPanel = ({ rock, renderSelection }: Props) => {
  const drainageView = rock.process_views.find(
    view => view.process === 'drainage'
  );
  const imbibitionView = rock.process_views.find(
    view => view.process === 'imbibition'
  );
  const pcView = rock.process_views.find(view => view.process === 'pc');

  if (!drainageView && !imbibitionView && !pcView) {
    return (
      <div data-testid={`ghe-page4-curves-${rock.rock_id}`}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No chart data available for this rock."
        />
      </div>
    );
  }

  return (
    <div
      data-testid={`ghe-page4-curves-${rock.rock_id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--ds-space-lg)'
      }}
    >
        {drainageView && (
          <>
            <LazyMount style={{ minHeight: 420 }}>
              <ChartResults
                containerWidth="100%"
                height="420px"
                title={`${rock.rock_id} - Drainage`}
                xAxis={drainageView.x_label}
                yAxis={drainageView.y_label}
                xAxisMin={0}
                xAxisMax={1}
                backgroundColor="var(--ds-color-white)"
                borderColor="var(--ds-emerald-zinc-100)"
                marginTop={0}
                data={mapSeries(drainageView.series)}
              />
            </LazyMount>
            {renderSelection?.('drainage')}
          </>
        )}
        {imbibitionView && (
          <>
            <LazyMount style={{ minHeight: 420 }}>
              <ChartResults
                containerWidth="100%"
                height="420px"
                title={`${rock.rock_id} - Imbibition`}
                xAxis={imbibitionView.x_label}
                yAxis={imbibitionView.y_label}
                xAxisMin={0}
                xAxisMax={1}
                backgroundColor="var(--ds-color-white)"
                borderColor="var(--ds-emerald-zinc-100)"
                marginTop={0}
                data={mapSeries(imbibitionView.series)}
              />
            </LazyMount>
            {renderSelection?.('imbibition')}
          </>
        )}
        {pcView && (
          <>
            <LazyMount style={{ minHeight: 420 }}>
              <ChartResults
                containerWidth="100%"
                height="420px"
                title={`${rock.rock_id} - Pc`}
                xAxis={pcView.x_label}
                yAxis={pcView.y_label}
                xAxisMin={0}
                xAxisMax={1}
                yAxisType="logarithmic"
                backgroundColor="var(--ds-color-white)"
                borderColor="var(--ds-emerald-zinc-100)"
                marginTop={0}
                data={mapSeries(pcView.series)}
              />
            </LazyMount>
            {renderSelection?.('pc')}
          </>
        )}
    </div>
  );
};
